import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import type {
  DispatchAppendNewLegCommand,
  DispatchAppendNewLegResponse,
  DispatchCreateCurrentQuoteRequest,
  DispatchCreateCurrentQuoteResponse,
  DispatchManifestDetailResponse,
  DispatchManifestTargetQuery,
  DispatchManifestTargetsResponse,
  DispatchRateQuoteResponse,
  DispatchRatingRequest,
} from '../models/dispatch-freight.models';

@Injectable({ providedIn: 'root' })
export class DispatchFreightApiService {
  static readonly RATE_ENDPOINT = '/api/rates/customer_rate_quote.php';
  static readonly CURRENT_QUOTES_ENDPOINT = '/api/quotes/current_quotes.php';
  static readonly APPEND_NEW_LEG_ENDPOINT = '/api/manifests/append_new_leg.php';
  static readonly MANIFEST_READ_ENDPOINT = '/api/manifests/manifest_read.php';

  constructor(private readonly http: HttpClient) {}

  rate(request: DispatchRatingRequest): Observable<DispatchRateQuoteResponse> {
    return this.http.post<DispatchRateQuoteResponse>(
      DispatchFreightApiService.RATE_ENDPOINT,
      request,
    );
  }

  createCurrentQuote(
    request: DispatchCreateCurrentQuoteRequest,
    idempotencyKey: string,
  ): Observable<DispatchCreateCurrentQuoteResponse> {
    return this.http.post<DispatchCreateCurrentQuoteResponse>(
      DispatchFreightApiService.CURRENT_QUOTES_ENDPOINT,
      request,
      {
        headers: new HttpHeaders().set('Idempotency-Key', idempotencyKey),
      },
    );
  }

  appendNewLeg(
    command: DispatchAppendNewLegCommand,
    idempotencyKey: string,
  ): Observable<DispatchAppendNewLegResponse> {
    return this.http.post<DispatchAppendNewLegResponse>(
      DispatchFreightApiService.APPEND_NEW_LEG_ENDPOINT,
      command.request,
      {
        params: new HttpParams().set('manifest_uuid', command.manifest_uuid),
        headers: new HttpHeaders().set('Idempotency-Key', idempotencyKey),
      },
    );
  }

  manifestTargets(
    query: DispatchManifestTargetQuery,
  ): Observable<DispatchManifestTargetsResponse> {
    let params = new HttpParams()
      .set('mode', 'targets')
      .set('customer_contact_uuid', query.customer_contact_uuid.trim());
    const search = query.search?.trim();
    if (search) {
      params = params.set('search', search);
    }
    if (query.limit !== undefined) {
      params = params.set('limit', String(query.limit));
    }

    return this.http.get<DispatchManifestTargetsResponse>(
      DispatchFreightApiService.MANIFEST_READ_ENDPOINT,
      { params },
    );
  }

  manifestDetail(manifestUuid: string): Observable<DispatchManifestDetailResponse> {
    return this.http.get<DispatchManifestDetailResponse>(
      DispatchFreightApiService.MANIFEST_READ_ENDPOINT,
      {
        params: new HttpParams()
          .set('mode', 'detail')
          .set('manifest_uuid', manifestUuid.trim()),
      },
    );
  }
}
